<template>
  <base-view id="home">

    <!-- Hero -->
    <section class="hero" :style="{ backgroundImage: 'url(' + heroImage + ')' }">
      <div class="hero-overlay" aria-hidden="true"></div>
      <div class="hero-inner">
        <div class="hero-text">
          <h1 class="hero-title">Start getting the benefit out of your Benefits Plan</h1>
          <p class="hero-sub">Rocky Mountain Reserve makes it simple to manage your FSA, HSA, HRA, COBRA, and more — backed by real people who pick up the phone.</p>
          <div class="hero-app">
            <span class="hero-app-label">
              <img src="@/assets/rmrAppStoreLogo.png" class="hero-app-logo" alt="" aria-hidden="true" />
              Download the RMR Benefits app
            </span>
            <div class="hero-app-stores">
              <div class="hero-app-store-col">
                <a href="https://apps.apple.com/us/app/rmr-benefits/id6479498415" target="_blank" rel="noopener noreferrer">
                  <img src="@/assets/appStoreBlack.svg" class="app-badge" alt="Download on the App Store" />
                </a>
                <img src="@/assets/appleQR.png" class="app-qr" alt="Apple App Store QR code" />
              </div>
              <div class="hero-app-store-col">
                <a href="https://play.google.com/store/apps/details?id=com.rockymountainreserve.rmr_app&hl=en_US&gl=US" target="_blank" rel="noopener noreferrer">
                  <img src="@/assets/googlePlayBlack.png" class="app-badge" alt="Get it on Google Play" />
                </a>
                <img src="@/assets/googleQR.png" class="app-qr" alt="Google Play QR code" />
              </div>
            </div>
          </div>
        </div>

        <div class="hero-card">
          <div class="hero-card-title">Quick Access</div>
          <div class="hero-card-divider" aria-hidden="true"></div>
          <router-link to="/login-participant" class="hero-card-btn hero-card-btn-primary">
            <span class="flaticon-lineal" aria-hidden="true"></span>
            Participant Login
          </router-link>
          <router-link to="/login-employer" class="hero-card-btn hero-card-btn-outline">
            <span class="flaticon-management" aria-hidden="true"></span>
            Employer Login
          </router-link>
          <div class="hero-card-divider" aria-hidden="true"></div>
          <div class="hero-card-links">
            <router-link to="/whats-covered" class="hero-card-link">What's Covered</router-link>
            <router-link to="/forms" class="hero-card-link">Forms &amp; Documents</router-link>
            <router-link to="/support-portal" class="hero-card-link">Support &amp; FAQ</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats band -->
    <section class="stats-band">
      <div class="stats-band-inner">
        <div class="stat-item">
          <span class="stat-value">71+</span>
          <span class="stat-label">Years of combined relationship manager experience</span>
        </div>
        <div class="stat-sep" aria-hidden="true"></div>
        <div class="stat-item">
          <span class="stat-value">93%</span>
          <span class="stat-label">Client Services Center satisfaction rate</span>
        </div>
        <div class="stat-sep" aria-hidden="true"></div>
        <div class="stat-item">
          <span class="stat-value">207k</span>
          <span class="stat-label">COBRA and cafeteria plans administered</span>
        </div>
      </div>
    </section>

    <!-- Employer reviews carousel -->
    <section class="reviews-section">
      <h2 class="section-heading">Trusted by Employers Across the Country</h2>
      <Carousel :reviews="reviews" />
    </section>

    <!-- Services -->
    <section class="services-section">
      <div class="services-inner">
        <h2 class="section-heading">What We Do</h2>
        <div class="services-grid">
          <router-link class="service-card" to="/services/fsa">
            <span class="service-icon flaticon-medical" aria-hidden="true"></span>
            <span class="service-name">Flexible Spending</span>
          </router-link>
          <router-link class="service-card" to="/services/hsa">
            <span class="service-icon flaticon-growth" aria-hidden="true"></span>
            <span class="service-name">Health Savings</span>
          </router-link>
          <router-link class="service-card" to="/services/hra">
            <span class="service-icon flaticon-refund" aria-hidden="true"></span>
            <span class="service-name">Health Reimbursement</span>
          </router-link>
          <router-link class="service-card" to="/services/transit">
            <span class="service-icon flaticon-bus-1" aria-hidden="true"></span>
            <span class="service-name">Transit / Parking</span>
          </router-link>
          <router-link class="service-card" to="/services/cobra">
            <span class="service-icon flaticon-files-and-folders" aria-hidden="true"></span>
            <span class="service-name">COBRA Administration</span>
          </router-link>
          <router-link class="service-card" to="/services/wellness">
            <span class="service-icon flaticon-apple" aria-hidden="true"></span>
            <span class="service-name">Wellness Accounts</span>
          </router-link>
          <router-link class="service-card" to="/services/dca">
            <span class="service-icon flaticon-lifetime" aria-hidden="true"></span>
            <span class="service-name">Dependent Care</span>
          </router-link>
          <router-link class="service-card" to="/services/cafeteria">
            <span class="service-icon flaticon-clipboard" aria-hidden="true"></span>
            <span class="service-name">Cafeteria Plans</span>
          </router-link>
          <router-link class="service-card" to="/services/lsa">
            <span class="service-icon flaticon-relaxing" aria-hidden="true"></span>
            <span class="service-name">Lifestyle Spending</span>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="testimonials-section">
      <div class="testimonials-inner">
        <h2 class="section-heading">What Participants Are Saying</h2>
        <p class="section-sub">Real feedback from the people we serve every day.</p>
        <div class="testimonials-grid">
          <div v-for="(t, i) in testimonials" :key="i" class="testimonial-card">
            <p class="testimonial-quote">"{{ t.quote }}"</p>
            <div class="testimonial-author">— {{ t.author }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Support CTA -->
    <section class="support-section">
      <div class="support-inner">
        <h2 class="section-heading">Support &amp; Resources</h2>
        <p class="section-sub">Find answers to common questions about your benefits plan.</p>
        <div v-if="featuredArticles.length" class="support-articles">
          <router-link
            v-for="article in featuredArticles"
            :key="article.id"
            :to="`/support-portal/articles/${article.id}`"
            class="support-article"
          >
            <span class="flaticon-support support-article-icon" aria-hidden="true"></span>
            {{ article.title }}
          </router-link>
        </div>
        <router-link to="/support-portal" class="support-cta">Visit Support Portal →</router-link>
      </div>
    </section>

  </base-view>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import BaseView from "@/components/layout/BaseView.vue";
import Carousel from "@/views/Home/components/Carousel.vue";

export default {
  name: "HomeRedesign",
  components: { BaseView, Carousel },
  created() {
    this.fetchZendeskData();
  },
  computed: {
    ...mapGetters(["promotedArticles"]),
    featuredArticles() {
      return (this.promotedArticles || []).slice(0, 4);
    }
  },
  methods: {
    ...mapActions(["fetchZendeskData"])
  },
  data() {
    return {
      heroImage: require("@/assets/bg.jpg"),
      testimonials: [
        { quote: "I called customer service, pressed 1 and a human named Johanna answered. I was stunned. I had a few quick questions regarding my FSA and received quick answers.", author: "David Buchanan" },
        { quote: "RMR has a good app and better customer service! Although AI is taking over, it's good to know you can speak with a live agent without waiting an hour", author: "Nathaniel Braithwaite" },
        { quote: "I called expecting a long wait and a phone tree, but I got a 30 second wait and a friendly, smart, and capable young lady who helped me set up direct deposit quickly.", author: "Gerry Baker" },
        { quote: "I have reached out to them a couple times and the service provider not only gave me the information but offered to personally handle the transaction. I was impressed to say the least.", author: "David Blackett" }
      ],
      reviews: [
        { company: "INSCRIPTA", quote: '"Rocky Mountain Reserve has been a great partner for our FSA, HSA and COBRA administration needs. Whenever a question arises, the team has been extremely responsive. "' },
        { company: "Kong Company", quote: '"Having a partner like Rocky Mountain Reserve to offset managing COBRA, HRA, and FSA is like having a magic wand. You can count on RMR to handle the administration seamlessly."' },
        { company: "Cosmic AES", quote: '"Rocky Mountain Reserve provides quality service by employing knowledgeable and hardworking staff. They are expeditious in their resolution of claims and efficient when resolving issues that rarely occur. Cosmic AES is proud to be partnered with RMR for 7 years straight"' },
        { company: "Moxie SoZo", quote: '"Our goal with selecting an FSA plan administrator was to find a company that efficiently provides an FSA plan without any hiccups — Rocky Mountain Reserve goes above & beyond this goal by providing a great plan and AMAZING customer service."' },
        { company: "Air Force Academy Athletics", quote: '"Our employees and I (as the plan administrator) always appreciate the friendly service and extremely fast turnaround times provided by Rocky Mountain Reserve!"' },
        { company: "ICON Fitness", quote: '"RMR is great to work with. They\'re always on the ball and they do all that they can, every time, to help our business run smoothly!"' }
      ]
    };
  }
};
</script>

<style scoped>
/* ── Hero ── */
.hero {
  position: relative;
  background-size: cover;
  background-position: center;
}
.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.75) 60%, rgba(255,255,255,0.5) 100%);
}
.hero-inner {
  position: relative;
  max-width: 72rem;
  margin: 0 auto;
  padding: 5rem 2rem;
  display: flex;
  align-items: center;
  gap: 3rem;
}
.hero-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.hero-title {
  margin: 0;
  font-size: 2.375rem;
  font-weight: 800;
  line-height: 1.2;
  color: #111827;
}
@media (min-width: 768px) {
  .hero-title { font-size: 2.875rem; }
}
.hero-sub {
  margin: 0;
  font-size: 1.0625rem;
  color: #374151;
  line-height: 1.65;
  max-width: 32rem;
}
.hero-app {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin-top: 0.5rem;
}
.hero-app-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
}
.hero-app-logo { width: 1.625rem; border-radius: 0.25rem; }
.hero-app-stores { display: flex; gap: 1.25rem; align-items: flex-start; }
.hero-app-store-col { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
.app-badge { height: 2.5rem; width: auto; display: block; }
.app-qr { width: 6rem; height: 6rem; background: #fff; padding: 0.25rem; border-radius: 0.375rem; border: 1px solid #e5e7eb; }

/* Quick access card */
.hero-card {
  width: 17rem;
  flex-shrink: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
}
.hero-card-title {
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9ca3af;
}
.hero-card-divider {
  height: 1px;
  background: #f3f4f6;
  margin: 0 -1.5rem;
}
.hero-card-btn {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 700;
  text-decoration: none;
  transition: opacity 0.15s;
}
.hero-card-btn:hover { opacity: 0.85; }
.hero-card-btn span { font-size: 1.125rem; }
.hero-card-btn-primary { background: #096843; color: #ffffff; }
.hero-card-btn-outline { background: #f0fdf4; color: #096843; border: 1.5px solid #bbf7d0; }
.hero-card-links {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.hero-card-link {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  text-decoration: none;
  padding: 0.25rem 0;
  border-bottom: 1px solid transparent;
  transition: color 0.15s;
}
.hero-card-link:hover { color: #096843; }

/* Stack on mobile */
@media (max-width: 767px) {
  .hero-inner { flex-direction: column; padding: 3rem 1.25rem; }
  .hero-card { width: 100%; }
}

/* ── Stats band ── */
.stats-band {
  background: #096843;
  padding: 2.5rem 2rem;
}
.stats-band-inner {
  max-width: 56rem;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.375rem;
  flex: 1;
  min-width: 10rem;
}
.stat-value { font-size: 2.25rem; font-weight: 800; color: #ffffff; line-height: 1; }
.stat-label { font-size: 0.875rem; color: rgba(255,255,255,0.8); line-height: 1.4; }
.stat-sep { width: 1px; height: 3rem; background: rgba(255,255,255,0.25); flex-shrink: 0; }
@media (max-width: 639px) { .stat-sep { display: none; } }

/* ── Shared section ── */
.section-heading {
  margin: 0 0 0.5rem;
  font-size: 1.75rem;
  font-weight: 800;
  color: #111827;
  text-align: center;
}
.section-sub {
  margin: 0 0 1.75rem;
  font-size: 1rem;
  color: #6b7280;
  text-align: center;
}

/* ── Reviews ── */
.reviews-section {
  padding: 1.75rem 2rem;
  background: #fafafa;
  border-bottom: 1px solid #e5e7eb;
}
.reviews-section .section-heading {
  font-size: 1rem;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
}

/* ── Services ── */
.services-section {
  padding: 3.5rem 2rem;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.services-inner { max-width: 64rem; margin: 0 auto; }
.services-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-top: 1.75rem;
}
@media (min-width: 640px)  { .services-grid { grid-template-columns: repeat(3, 1fr); } }
@media (min-width: 1024px) { .services-grid { grid-template-columns: repeat(4, 1fr); } }
.service-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
  padding: 1.5rem 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  text-decoration: none;
  color: #111827;
  font-weight: 600;
  font-size: 0.9375rem;
  text-align: center;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.service-card:hover { background: #096843; border-color: #096843; color: #ffffff; }
.service-card:hover .service-icon { color: #ffffff; }
.service-icon { font-size: 2rem; color: #096843; transition: color 0.15s; }

/* ── Testimonials ── */
.testimonials-section { padding: 3.5rem 2rem; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
.testimonials-inner { max-width: 56rem; margin: 0 auto; }
.testimonials-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 1.75rem;
}
@media (min-width: 640px) { .testimonials-grid { grid-template-columns: repeat(2, 1fr); } }
.testimonial-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.testimonial-quote { flex: 1; font-size: 0.9375rem; line-height: 1.6; color: #374151; margin: 0; }
.testimonial-author { font-size: 0.8125rem; font-weight: 700; color: #096843; }

/* ── Support ── */
.support-section { padding: 3.5rem 2rem; background: #f0fdf4; }
.support-inner { max-width: 44rem; margin: 0 auto; text-align: center; }
.support-articles {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 1.75rem 0;
  text-align: left;
}
.support-article {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: #ffffff;
  border: 1px solid #bbf7d0;
  border-radius: 0.625rem;
  text-decoration: none;
  color: #111827;
  font-size: 0.9375rem;
  font-weight: 500;
  transition: background 0.15s, color 0.15s;
}
.support-article:hover { background: #096843; color: #ffffff; }
.support-article:hover .support-article-icon { color: #ffffff; }
.support-article-icon { font-size: 1.125rem; color: #096843; flex-shrink: 0; transition: color 0.15s; }
.support-cta {
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 2rem;
  background: #096843;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 999px;
  text-decoration: none;
  transition: opacity 0.15s;
}
.support-cta:hover { opacity: 0.88; }
</style>
